/**
 * Building blocks shared by the supply-finance pages (Procurement, Forecast, Invoicing): section cards,
 * toolbar search / segmented control / filter chips, the table shell and pager, progress and stacked
 * bars, date cells with a relative sub-line, and the empty-value dash. They sit on the app foundation
 * (FilterChip, Button, tokens, formatters) — no page-local colours.
 */
import { forwardRef, useEffect, useState } from 'react';
import { HardHat, Warehouse } from 'lucide-react';
import { SearchField as UiSearchField } from '@/components/ui/search-field';
import { SystemChip as UiSystemChip } from '@/components/ui/system-chip';
import { cn } from '@/lib/utils';
import { EMPTY, ageDays, formatDate, formatDays, formatNumber } from '@/lib/format';
import { TONE_TEXT_CLASS } from '@/lib/status';

/* ------------------------------------------------------------------ layout */

/** True while the media query matches. False where matchMedia is missing (tests). */
export function useMediaQuery(query) {
  const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? Boolean(window.matchMedia(query)?.matches)
    : false);
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    if (!list) return undefined;
    const update = () => setMatches(Boolean(list.matches));
    update();
    if (list.addEventListener) list.addEventListener('change', update); else list.addListener?.(update);
    return () => {
      if (list.removeEventListener) list.removeEventListener('change', update); else list.removeListener?.(update);
    };
  }, [query]);
  return matches;
}

/** Phones (the 390px mockups): lists instead of wide tables. */
export const useIsPhone = () => useMediaQuery('(max-width: 639px)');

export function SfCard({ as: Tag = 'section', className, children, ...props }) {
  return (
    <Tag className={cn('min-w-0 rounded-card border border-line bg-surface text-ink shadow-card', className)} {...props}>
      {children}
    </Tag>
  );
}

/** Card head: title + muted count / sub on the left, small actions on the right. */
export function CardHead({ icon: Icon, iconTone, title, sub, right, className, titleAs: TitleTag = 'h2', children }) {
  return (
    <div className={cn('flex min-h-[52px] flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-line-2 px-4 py-3 sm:px-[18px]', className)}>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        {Icon ? (
          <span className={cn('ui-icon-tile h-7 w-7 rounded-lg', iconTone ? `tone-${iconTone}` : 'tone-navy')} aria-hidden="true">
            <Icon className="h-[15px] w-[15px]" />
          </span>
        ) : null}
        <TitleTag className="font-heading text-[15px] font-bold text-ink">{title}</TitleTag>
        {sub ? <span className="text-[12.5px] text-ink-3">{sub}</span> : null}
        {children}
      </div>
      {right ? <div className="flex min-w-0 flex-wrap items-center gap-2">{right}</div> : null}
    </div>
  );
}

export function CardFoot({ className, children }) {
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-t border-line-2 px-4 py-2.5 text-[12.5px] text-ink-3 sm:px-[18px]', className)}>
      {children}
    </div>
  );
}

export function UpperLabel({ className, children }) {
  return <p className={cn('text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3', className)}>{children}</p>;
}

/** Inline navy text button (links inside sentences and card heads). */
export const LinkButton = forwardRef(function LinkButton({ className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn('inline-flex items-center gap-1 rounded text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
      {...props}
    >
      {children}
    </button>
  );
});

/* ------------------------------------------------------------------ values */

/** The empty-value dash: decoration colour, announced as "None". */
export function Dash({ className, label = 'None' }) {
  return (
    <span className={cn('text-ink-4', className)}>
      <span aria-hidden="true">{EMPTY}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Missing-but-expected data ("No project linked", "Supplier missing"). */
export function MissingText({ className, children }) {
  return <span className={cn('text-[11.5px] font-semibold text-status-amber-ink', className)}>{children}</span>;
}

export const tabular = 'tabular-nums';

/** Units next to Chivo numbers ("AED 475,000") are set in the body font, like StatCard. */
export const BODY_FONT = { fontFamily: 'Manrope, system-ui, sans-serif' };

/**
 * A calendar date ("12 Mar 2026") with a relative sub-line. `mode`:
 *  - 'due'  (deadlines / expected dates): past → red "202 d late"; within `soonDays` → amber "in 12 d"; else muted "in 40 d"
 *  - 'ago'  (things that happened): muted "67 d ago"
 * `done` drops the lateness (a delivered LPO is not late).
 */
export function DateCell({ value, mode = 'due', soonDays = 30, done = false, now, className, emptyLabel = 'No date', subClassName }) {
  const days = ageDays(value, { now });
  if (days === null) {
    return value ? <span className={className}>{String(value)}</span> : <MissingText className={className}>{emptyLabel}</MissingText>;
  }
  const { text, tone } = relativeDayText(days, { mode, soonDays, done });
  return (
    <div className={cn('min-w-0 leading-tight', className)}>
      <div className="whitespace-nowrap text-[13px] text-ink">{formatDate(value, { now })}</div>
      {text ? (
        <div className={cn('mt-0.5 whitespace-nowrap text-[12px]', tone ? cn('font-semibold', TONE_TEXT_CLASS[tone]) : 'text-ink-3', subClassName)}>{text}</div>
      ) : null}
    </div>
  );
}

/** days = ageDays(date): positive = in the past. */
export function relativeDayText(days, { mode = 'due', soonDays = 30, done = false } = {}) {
  if (days === null || days === undefined) return { text: '', tone: null };
  if (mode === 'ago') {
    if (days === 0) return { text: 'today', tone: null };
    if (days < 0) return { text: `in ${formatDays(days)}`, tone: null };
    return { text: `${formatDays(days)} ago`, tone: null };
  }
  if (done) return { text: '', tone: null };
  if (days > 0) return { text: `${formatDays(days)} late`, tone: 'red' };
  if (days === 0) return { text: 'due today', tone: 'amber' };
  return { text: `in ${formatDays(days)}`, tone: -days <= soonDays ? 'amber' : null };
}

/**
 * Where a delivery went: a neutral tag, never a status colour. Always the same two words everywhere —
 * "Warehouse" / "Direct to site" (the Receive delivery form uses them too); the store a warehouse delivery
 * went into is the tooltip, or a second line with `showLocation`.
 */
export function DestinationTag({ type, location, className, showLocation = false }) {
  const toSite = type === 'to_site';
  const Icon = toSite ? HardHat : Warehouse;
  const label = toSite ? 'Direct to site' : 'Warehouse';
  const store = toSite ? '' : String(location || '').trim();
  const tag = (
    <span
      className={cn('inline-flex h-[22px] max-w-full items-center gap-1.5 whitespace-nowrap rounded-md border border-line bg-surface-2 px-2 text-[11.5px] font-semibold text-ink-2', !showLocation && className)}
      title={toSite ? 'Delivered straight to the project site' : `Received into ${store || 'the warehouse'}`}
    >
      <Icon className="h-3 w-3 shrink-0 text-ink-3" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  );
  if (!showLocation || !store) return showLocation ? <span className={cn('inline-flex max-w-full', className)}>{tag}</span> : tag;
  return (
    <span className={cn('inline-flex min-w-0 max-w-full flex-col items-start gap-0.5', className)}>
      {tag}
      <span className="max-w-full truncate text-[11.5px] leading-tight text-ink-3">{store}</span>
    </span>
  );
}

/** Small neutral tag ("default", "Held"). */
export function Tag({ className, children, title }) {
  return (
    <span title={title} className={cn('inline-flex h-[20px] items-center whitespace-nowrap rounded-md border border-line bg-surface-2 px-1.5 text-[11px] font-semibold text-ink-3', className)}>
      {children}
    </span>
  );
}

/** Solid system-type chip (BMS / LCS / HA KNX …) — the foundation chip the Projects list and the project
    header use (components/ui/system-chip), so a system has the same colour on every page. */
export function SystemChip({ system, className }) {
  return <UiSystemChip systemType={system} className={cn('h-[20px] min-w-[38px] rounded-[6px]', className)} />;
}

/* ------------------------------------------------------------------ bars */

const FILL = {
  navy: 'bg-brand',
  green: 'bg-status-green',
  amber: 'bg-status-amber',
  red: 'bg-status-red',
  blue: 'bg-status-blue',
};

/** 6px progress bar: navy while partial, green at 100%, empty track at 0. */
export function ProgressBar({ value = 0, tone, className, label }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const fill = tone || (pct >= 100 ? 'green' : 'navy');
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('block h-1.5 min-w-[40px] overflow-hidden rounded-full bg-[color:var(--line)]', className)}
    >
      <span className={cn('block h-full rounded-full', FILL[fill] || FILL.navy)} style={{ width: `${pct}%` }} />
    </span>
  );
}

/** Progress + "38/98" label (delivered of ordered). */
export function QtyProgress({ done = 0, total = 0, className, barClassName = 'w-[72px]', label }) {
  const pct = total > 0 ? (done / total) * 100 : 0;
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <ProgressBar value={pct} className={cn('shrink-0', barClassName)} label={label || `${formatNumber(done)} of ${formatNumber(total)} delivered`} />
      <span className="whitespace-nowrap text-[12px] tabular-nums text-ink-2">{formatNumber(done)}/{formatNumber(total)}</span>
    </span>
  );
}

/**
 * Stacked bar: segments [{ key, value, className, label }] as shares of `total` (defaults to their sum).
 * Received = green, awaiting = amber, not started = neutral track colour.
 */
export function StackBar({ segments = [], total, className, label }) {
  const sum = total ?? segments.reduce((acc, segment) => acc + Math.max(0, Number(segment.value) || 0), 0);
  return (
    <div
      role="img"
      aria-label={label || segments.map((segment) => `${segment.label}: ${formatNumber(segment.value, { decimals: 0 })}`).join(', ')}
      className={cn('flex h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--line)]', className)}
    >
      {sum > 0 ? segments.map((segment) => {
        const pct = (Math.max(0, Number(segment.value) || 0) / sum) * 100;
        if (pct <= 0) return null;
        return <span key={segment.key} className={cn('h-full', segment.className)} style={{ width: `${pct}%` }} />;
      }) : null}
    </div>
  );
}

export const STACK_CLASS = {
  received: 'bg-status-green',
  awaiting: 'bg-status-amber',
  notStarted: 'bg-[color:var(--line)]',
};

/* ------------------------------------------------------------------ toolbar */

/** Toolbar search. `shortcut` makes "/" focus it from anywhere on the page (one per page). */
/** Toolbar search (components/ui/search-field). Pass `shortcut` for the page's main search ("/" focuses it). */
export const SearchField = forwardRef(function SearchField({ shortcut = false, ...props }, ref) {
  return <UiSearchField ref={ref} shortcut={shortcut} {...props} />;
});

/** Segmented control: one of N views (components/ui/segmented). */
export { Segmented } from '@/components/ui/segmented';

/**
 * Toolbar filter chip with a picker: "Project ⌄" when unset, "Project: One river ×" when set
 * (components/ui/chip-select). options: [{ value, label, count, muted }].
 */
export { ChipSelect } from '@/components/ui/chip-select';

/* ------------------------------------------------------------------ table */

export const tableClass = 'w-full border-separate border-spacing-0 text-[13px] text-ink';
export const thClass = 'h-[38px] whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left text-[11.5px] font-bold uppercase tracking-[0.04em] text-ink-3 first:pl-4 last:pr-4 sm:first:pl-[18px] sm:last:pr-[18px]';
export const tdClass = 'h-12 border-b border-line-2 px-3 align-middle first:pl-4 last:pr-4 sm:first:pl-[18px] sm:last:pr-[18px]';
export const numClass = 'text-right tabular-nums';
export const subClass = 'mt-0.5 block text-[12px] text-ink-3';

export function TableScroll({ className, children }) {
  return <div className={cn('overflow-x-auto', className)}>{children}</div>;
}

/** ‹ 1 … 4 5 6 … 15 › (components/ui/pager). */
export { Pager } from '@/components/ui/pager';

/** Pluralise a count: plural(1, 'item') → '1 item'; plural(5, 'item') → '5 items'. */
export function plural(count, singular, pluralForm) {
  const n = Number(count) || 0;
  return `${formatNumber(n, { maxDecimals: 0 })} ${n === 1 ? singular : (pluralForm || `${singular}s`)}`;
}

/**
 * Phone KPI row (the 390px mockups): three compact tiles — big number + one-line label — instead of
 * four full-width cards that would fill the first screen. items: [{ key, value, prefix, label, tone, onClick }].
 */
export function PhoneKpis({ items, testId }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testId}>
      {items.map((item) => {
        const Tag = item.onClick ? 'button' : 'div';
        return (
          <Tag
            key={item.key}
            type={item.onClick ? 'button' : undefined}
            onClick={item.onClick}
            className="min-w-0 rounded-[14px] border border-line bg-surface px-2.5 py-2.5 text-left shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className={cn('flex items-baseline gap-1 font-heading text-[22px] font-black leading-tight tracking-[-0.02em]', item.tone ? TONE_TEXT_CLASS[item.tone] : 'text-ink')}>
              {item.prefix ? <small className="text-[11px] font-bold tracking-normal text-ink-3" style={BODY_FONT}>{item.prefix}</small> : null}
              <span className="truncate tabular-nums">{item.value}</span>
            </span>
            <span className="block text-[12px] leading-snug text-ink-3">{item.label}</span>
          </Tag>
        );
      })}
    </div>
  );
}
